import React from 'react';
import { useAppContext } from '@prisjakt/ui-app-context';
import ChevronRight from 'mdi-react/ChevronRightIcon';
import PriceNotice from '../price-notice';
import ShippingLabel from '../../../shipping-label';
import StoreBadgeList from '../../../store-badge-list';
import StoreInfoList from './store-info-list';
import StoreLink from './store-link';
import RecommendedRetailer from './recommended-retailer';
import StoreReviews from './store-reviews';

import { BadgesFragment, Price, ProductPageFragment } from '../../../../types';
import { SortOption } from '../../../../areas/product/components/sort-options/types';
import { ProductPriceType } from '../../../../areas/product/types';
import { Rating } from '../../../../../components/rating/src/types';

import * as S from './styles';

type OwnProps = {
  brand?: ProductPageFragment['brand'] | null;
  brandRecommendedRetailer: boolean;
  currentSort: SortOption;
  deliveryOptions: Price['deliveryOptions'];
  price: Price['price'];
  priceId?: string | null;
  showPriceListRatingExperiment: boolean;
  priceType?: ProductPriceType | null;
  rating?: Rating;
  showPriceListExpandedPriceRowExperiment: boolean;
  store: NonNullable<Price['store']>;
  storeBadges?: BadgesFragment['storeBadges'];
  storeInfo?: (string | null)[] | null;
};

const StoreInfo = ({
  brand,
  brandRecommendedRetailer,
  currentSort,
  deliveryOptions,
  price,
  priceId,
  showPriceListRatingExperiment,
  priceType,
  rating,
  showPriceListExpandedPriceRowExperiment,
  store,
  storeBadges,
  storeInfo,
}: OwnProps) => {
  const { t } = useAppContext();

  return (
    <S.ExpandedInfo>
      <section>
        {showPriceListExpandedPriceRowExperiment && (
          <S.Title>{store.name}</S.Title>
        )}
        {showPriceListExpandedPriceRowExperiment &&
        store.userReviewSummary.countTotal &&
        store.userReviewSummary.countTotal > 0 ? (
          <StoreReviews
            featured={Boolean(store.featured)}
            showPriceListRatingExperiment={showPriceListRatingExperiment}
            rating={rating}
            reviewCount={store.userReviewSummary.countTotal}
            storeLink={store.pathName}
          />
        ) : null}

        <StoreInfoList
          storeInfo={storeInfo}
          showPriceListExpandedPriceRowExperiment={
            showPriceListExpandedPriceRowExperiment
          }
        />

        {!showPriceListExpandedPriceRowExperiment && (
          <StoreLink link={store.pathName} name={store.name} />
        )}

        {showPriceListExpandedPriceRowExperiment && (
          <>
            <S.HomeDelivery deliveryOptions={deliveryOptions} />
            <S.LocationAndCurrencyInfo
              countryCode={store.countryCode}
              currency={store.currency}
              marketplace={Boolean(store.marketplace)}
              id={priceId || store.id.toString()}
              storeName={store.name}
              minify
            />
          </>
        )}

        {brandRecommendedRetailer && brand && (
          <RecommendedRetailer
            {...brand}
            showPriceListExpandedPriceRowExperiment={
              showPriceListExpandedPriceRowExperiment
            }
          />
        )}
      </section>
      <section>
        {!showPriceListExpandedPriceRowExperiment && (
          <>
            {price && price.inclShipping && (
              <S.StoreInfoRowItem>
                <ShippingLabel
                  toneDown
                  exclShipping={price.exclShipping || 0}
                  inclShipping={price.inclShipping}
                  currentSort={currentSort}
                />
              </S.StoreInfoRowItem>
            )}

            {priceType && priceType !== 'normal' && (
              <S.StoreInfoRowItem>
                <PriceNotice type={priceType} />
              </S.StoreInfoRowItem>
            )}
            {storeBadges && <StoreBadgeList badges={storeBadges} />}
          </>
        )}
      </section>
      {showPriceListExpandedPriceRowExperiment && (
        <S.StoreLinkWrapper>
          <S.StoreLink
            color="link"
            icon={<ChevronRight />}
            iconPosition="right"
            href={store.pathName}
            ariaLabel={t('product', 'store-info.more-about-store', {
              store_name: store.name,
            })}
            data-test="StoreInfoLink"
          >
            {t('product', 'store-info.more-about-store', {
              store_name: store.name,
            })}
          </S.StoreLink>
        </S.StoreLinkWrapper>
      )}
    </S.ExpandedInfo>
  );
};

export default StoreInfo;
